import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {AbortController | null} */
  #focusTrapAbortController = null;

  /** @type {HTMLElement | null} */
  #focusReturnElement = null;

  /** @type {number | null} */
  #focusFrame = null;

  /** @type {number | null} */
  #focusRestoreTimeout = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleAccessibilityOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleAccessibilityClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleAccessibilityOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleAccessibilityClose);
    this.#historyAbortController?.abort();
    this.#focusTrapAbortController?.abort();
    if (this.#focusFrame !== null) cancelAnimationFrame(this.#focusFrame);
    if (this.#focusRestoreTimeout !== null) window.clearTimeout(this.#focusRestoreTimeout);
  }

  showDialog() {
    const { dialog } = this.refs;

    if (!dialog?.open && !this.#focusReturnElement && document.activeElement instanceof HTMLElement) {
      this.#focusReturnElement = document.activeElement;
    }

    super.showDialog();
  }

  #handleAccessibilityOpen = () => {
    const { dialog } = this.refs;
    if (!dialog) return;

    this.#focusTrapAbortController?.abort();
    this.#focusTrapAbortController = new AbortController();
    dialog.addEventListener('keydown', this.#handleFocusTrap, {
      capture: true,
      signal: this.#focusTrapAbortController.signal,
    });

    if (this.#focusFrame !== null) cancelAnimationFrame(this.#focusFrame);
    this.#focusFrame = requestAnimationFrame(() => {
      this.#focusFrame = null;
      const closeButton = dialog.querySelector('.cart-drawer__close-button');
      if (closeButton instanceof HTMLElement) closeButton.focus({ preventScroll: true });
    });
  };

  #handleAccessibilityClose = () => {
    this.#focusTrapAbortController?.abort();
    this.#focusTrapAbortController = null;
    if (this.#focusFrame !== null) cancelAnimationFrame(this.#focusFrame);

    const returnElement = this.#focusReturnElement;
    this.#focusReturnElement = null;

    const restoreFocus = () => {
      const fallback = this.querySelector('[data-testid="cart-drawer-trigger"]');
      const focusTarget = returnElement?.isConnected ? returnElement : fallback;
      if (!(focusTarget instanceof HTMLElement)) return;

      const activeElement = document.activeElement;
      const focusWasLost =
        !activeElement ||
        activeElement === document.body ||
        activeElement === document.documentElement ||
        activeElement === this.refs.dialog ||
        this.refs.dialog?.contains(activeElement) ||
        activeElement.id === 'INDShadowRootHost';

      if (focusWasLost || activeElement === focusTarget) {
        focusTarget.focus({ preventScroll: true });
      }
    };

    this.#focusFrame = requestAnimationFrame(() => {
      this.#focusFrame = null;
      restoreFocus();
    });

    if (this.#focusRestoreTimeout !== null) window.clearTimeout(this.#focusRestoreTimeout);
    this.#focusRestoreTimeout = window.setTimeout(() => {
      this.#focusRestoreTimeout = null;
      restoreFocus();
    }, 175);
  };

  #handleFocusTrap = (event) => {
    if (event.key !== 'Tab') return;

    const { dialog } = this.refs;
    if (!dialog?.open) return;

    const focusable = [...dialog.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )].filter((element) => {
      if (!(element instanceof HTMLElement)) return false;
      if (element.hidden || element.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
      return element.getClientRects().length > 0;
    });

    if (!focusable.length) {
      event.preventDefault();
      dialog.focus({ preventScroll: true });
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (!dialog.contains(active)) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    } else if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus({ preventScroll: true });
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
  };

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      const scrollY = window.scrollY;
      const canControlScrollRestoration = 'scrollRestoration' in history;
      const previousScrollRestoration = canControlScrollRestoration ? history.scrollRestoration : null;
      let didRestoreScroll = false;

      if (canControlScrollRestoration) history.scrollRestoration = 'manual';

      const restoreScroll = () => {
        if (didRestoreScroll) return;
        didRestoreScroll = true;

        window.requestAnimationFrame(() => {
          window.scrollTo({ top: scrollY, behavior: 'instant' });
          if (canControlScrollRestoration && previousScrollRestoration) {
            history.scrollRestoration = previousScrollRestoration;
          }
        });
      };

      window.addEventListener('popstate', restoreScroll, { once: true });
      history.back();
      window.setTimeout(restoreScroll, 150);
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
